// Feature flag wrapper — Sprint 3 infrastructure (NOT yet used for experiments).
//
// Thin abstraction over PostHog feature flags so product code never imports
// posthog-js directly. Provides:
//   - synchronous `isEnabled(key, default)` for no-flicker reads
//   - `onReady(cb)` for code that must wait for the first flag payload
//   - `override(key, value)` for QA / debug harness
//
// All overrides live in sessionStorage so they don't leak across users.

import posthog from "posthog-js";
import { metrics } from "./metrics";

const OVERRIDE_KEY = "nr_flag_overrides_v1";

type FlagValue = boolean | string;

function readOverrides(): Record<string, FlagValue> {
  try {
    const raw = sessionStorage.getItem(OVERRIDE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, FlagValue>) : {};
  } catch {
    return {};
  }
}

function writeOverrides(map: Record<string, FlagValue>): void {
  try {
    sessionStorage.setItem(OVERRIDE_KEY, JSON.stringify(map));
  } catch {
    /* noop */
  }
}

let ready = false;
const readyListeners = new Set<() => void>();

/** Called by PostHog provider once `onFeatureFlags` fires. */
export function _markFlagsReady(): void {
  if (ready) return;
  ready = true;
  for (const cb of readyListeners) {
    try { cb(); } catch { /* noop */ }
  }
}

export const flags = {
  isReady(): boolean {
    return ready;
  },

  onReady(cb: () => void): () => void {
    if (ready) { try { cb(); } catch { /* noop */ } return () => undefined; }
    readyListeners.add(cb);
    return () => readyListeners.delete(cb);
  },

  /**
   * Synchronous boolean read. Returns `defaultValue` if flags haven't loaded
   * yet — guarantees zero visual flicker in UI gated by this call.
   */
  isEnabled(key: string, defaultValue = false): boolean {
    const overrides = readOverrides();
    if (key in overrides) return Boolean(overrides[key]);
    if (!ready) return defaultValue;
    try {
      const v = posthog.isFeatureEnabled(key);
      return v ?? defaultValue;
    } catch {
      metrics.providerFailure("posthog");
      return defaultValue;
    }
  },

  /**
   * Multivariate read. Returns the variant key (e.g. "control" | "v1" | "v2")
   * or `defaultValue` if not yet loaded / no flag defined.
   */
  getVariant(key: string, defaultValue = "control"): string {
    const overrides = readOverrides();
    if (key in overrides) return String(overrides[key]);
    if (!ready) return defaultValue;
    try {
      const v = posthog.getFeatureFlag(key);
      if (v == null) return defaultValue;
      if (typeof v === "boolean") return v ? "enabled" : "control";
      return String(v);
    } catch {
      metrics.providerFailure("posthog");
      return defaultValue;
    }
  },

  /**
   * QA / debug override. Persists for this tab only; cleared on tab close.
   * Pass `value=null` to remove an override.
   */
  override(key: string, value: FlagValue | null): void {
    const map = readOverrides();
    if (value === null) delete map[key];
    else map[key] = value;
    writeOverrides(map);
  },

  clearOverrides(): void {
    try { sessionStorage.removeItem(OVERRIDE_KEY); } catch { /* noop */ }
  },

  /** Returns the merged view (override > live > undefined). For debug only. */
  snapshot(): Record<string, FlagValue | undefined> {
    const live = ready ? (posthog.featureFlags?.getFlagVariants?.() as Record<string, FlagValue> | undefined) ?? {} : {};
    return { ...live, ...readOverrides() };
  },
};
